/**
 * Tier names for the current organization.
 *
 * Proposals always store tiers under the fixed slugs good/better/best, but what
 * reps and customers see is configurable in Proposal Settings. Any authenticated
 * screen that shows a tier should render `labels[tier.tier_type]` rather than a
 * hardcoded "Good"/"Better"/"Best".
 *
 * The public proposal page can't use this — it isn't authenticated — and reads
 * `proposal.tier_labels` off the payload instead.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { proposalsApi } from '../lib/proposalsApi';
import { DEFAULT_TIER_LABELS } from '../types/proposals';
import type { ProposalTierLabel, TierLabelMap } from '../types/proposals';

export const TIER_LABELS_QUERY_KEY = ['proposal-tier-labels'] as const;

const toLabelMap = (rows: ProposalTierLabel[]): TierLabelMap => {
  const map = { ...DEFAULT_TIER_LABELS };
  rows.forEach((row) => {
    if (row.label) {
      map[row.tier_type] = row.label;
    }
  });
  return map;
};

/**
 * Returns the org's tier names, falling back to Good/Better/Best while the
 * request is in flight or if it fails — a tier chip should never render blank.
 */
export function useTierLabels() {
  const { data, isLoading, error } = useQuery({
    queryKey: TIER_LABELS_QUERY_KEY,
    queryFn: () => proposalsApi.listTierLabels(),
    // Tier names change about once in a company's lifetime
    staleTime: 5 * 60 * 1000,
  });

  // Stable reference — callers put `labels` in effect dependency arrays, and a
  // fresh object every render would re-run those effects endlessly
  const labels = useMemo(() => (data ? toLabelMap(data) : DEFAULT_TIER_LABELS), [data]);

  return {
    labels,
    rows: data ?? [],
    isLoading,
    error,
  };
}
